import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { EQuestionnaireActions, IQuestionnaireStore } from './questionnaire.state';
import { SELECT_QUESTIONNAIRE_STATE } from './questionnaire.selectors';
import { REMOVE_LAST_RESPONSE, SET_QUESTION } from './questionnaire.actions';
import { GuardService } from '@app/core';
import { QuestionnaireService } from '../services/questionnaire.service';
import { PartialUrl } from 'src/app/core/enums/partial-url.enum';

/**
 * Questionnaire Home effects
 */
@Injectable()
export class QuestionnaireEffects {
	/**
	 * Loads the questions
	 */
	LOAD_QUESTIONS$ = createEffect(() =>
		this._actions$.pipe(
			ofType(EQuestionnaireActions.LOAD_QUESTIONS),
			switchMap(() =>
				forkJoin({
					questions: this._questionnaireService.getQuestions(),
					baremos: this._questionnaireService.getBaremos(),
					refusals: this._questionnaireService.getRefusals(),
				}).pipe(
					map(({ questions, baremos, refusals }) => ({ type: EQuestionnaireActions.LOAD_QUESTIONS_SUCCESS, questions, baremos, refusals })),
					catchError(() => of({ type: EQuestionnaireActions.LOAD_QUESTIONS_FAIL })),
				),
			),
		),
	);

	/**
	 * Initializes the questionnaire
	 */
	INIT_QUESTIONNAIRE$ = createEffect(() =>
		this._actions$.pipe(
			ofType(EQuestionnaireActions.INIT_QUESTIONNAIRE),
			withLatestFrom(this._store.select(<any>SELECT_QUESTIONNAIRE_STATE)),
			switchMap(([, state]) => {
				return this._questionnaireService.getFirstQuestion(<IQuestionnaireStore>state).pipe(
					map((payload: number) => ({ type: EQuestionnaireActions.SET_QUESTION, payload })),
					catchError(() => of({ type: EQuestionnaireActions.LOAD_QUESTIONS_FAIL })),
				);
			}),
		),
	);

	/**
	 * Gets the next quetsion
	 */
	NEXT_QUESTION$ = createEffect(() =>
		this._actions$.pipe(
			ofType(EQuestionnaireActions.NEXT_QUESTION),
			withLatestFrom(this._store.select(<any>SELECT_QUESTIONNAIRE_STATE)),
			switchMap(([, state]) => {
				const index = this._questionnaireService.getNextQuestion(<IQuestionnaireStore>state);
				return of({ type: EQuestionnaireActions.SET_QUESTION, payload: index });
			}),
		),
	);

	/**
	 * Gets the previous quetsion
	 */
	PREVIOUS_QUESTION$ = createEffect(() =>
		this._actions$.pipe(
			ofType(EQuestionnaireActions.PREVIOUS_QUESTION),
			withLatestFrom(this._store.select(<any>SELECT_QUESTIONNAIRE_STATE)),
			switchMap(([, state]) => {
				const index = this._questionnaireService.getPreviousQuestion(<IQuestionnaireStore>state);
				return [SET_QUESTION({ payload: index }), REMOVE_LAST_RESPONSE()];
			}),
		),
	);

	/**
	 * Generates the questions
	 */
	GENERATE_QUESTIONS$ = createEffect(() =>
		this._actions$.pipe(
			ofType(EQuestionnaireActions.GENERATE_QUESTIONS),
			withLatestFrom(this._store.select(<any>SELECT_QUESTIONNAIRE_STATE)),
			switchMap(([, state]) => {
				const responses = (state as IQuestionnaireStore)?.responses;
				const questions = this._questionnaireService.GENERATE_QUESTIONS(responses);
				return of({ type: EQuestionnaireActions.SAVE_QUESTIONS, payload: questions });
			}),
		),
	);

	/**
	 * Sets the questions
	 */
	SET_QUESTION$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(EQuestionnaireActions.SET_QUESTION),
				tap(({ payload }) => {
					this._guardService.allowNavigationThroughQuestionnaire();
					if (payload === -1) {
						this._router.navigateByUrl(PartialUrl.QUESTIONNAIRE);
					} else if (payload === null) {
						this._router.navigateByUrl(`${PartialUrl.QUESTIONNAIRE}${PartialUrl.RESULT}`);
					} else {
						this._router.navigateByUrl(`${PartialUrl.QUESTIONNAIRE}/step/${payload}`);
					}
				}),
			),
		{ dispatch: false },
	);

	/**
	 * Cancels the questionnaire
	 */
	CANCEL$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(EQuestionnaireActions.CANCEL_QUESTIONNAIRE),
				withLatestFrom(this._store.select(<any>SELECT_QUESTIONNAIRE_STATE)),
				tap(() => {
					this._router.navigateByUrl(PartialUrl.HOME);
				}),
			),
		{ dispatch: false },
	);

	/**
	 * Questionnaire Home effects constructor
	 *
	 * @param _actions$ actions
	 * @param _questionnaireService home service
	 * @param _store store
	 * @param _router router
	 * @param _guardService guard service
	 */
	constructor(
		private _actions$: Actions,
		private _questionnaireService: QuestionnaireService,
		private _store: Store,
		private _router: Router,
		private _guardService: GuardService,
	) { }
}
